import React, { useState, useEffect, useCallback, useMemo, useRef, useImperativeHandle, forwardRef } from 'react';
import BaseButton from 'shared/buttons';
import { RadioButton } from '@progress/kendo-react-inputs';
import { getLegacyDetails } from 'src/fetch/organizations';
import { useQuery } from '@tanstack/react-query';

const OrganizationPrimarySelection = forwardRef(({
  loadExistingLegacyDetails = false, v2Organization, v1Organizations, onSelected, onSave, handleUnlink,
}, ref) => {
  const [grouped, setGrouped] = useState({});
  const [primaries, setPrimaries] = useState({});
  const [currentPrimaries, setCurrentPrimaries] = useState();
  const allOrgs = useRef();

  const loadLegacyDetails = useCallback(() => getLegacyDetails(v2Organization?.id), [v2Organization]);

  const { data } = useQuery({
    queryKey: ['dashboard-settings-by-market', loadLegacyDetails],
    queryFn: loadLegacyDetails,
    refetchInterval: false,
    refetchOnMount: true,
    refetchIntervalInBackground: false,
    refetchOnWindowFocus: false,
    enabled: loadExistingLegacyDetails,
  });

  useEffect(() => {
    const grouped = {};
    const primaries = {};
    const existingRecs = data != null
    && Array.isArray(data) ? data?.map((o) => ({ ...o, id: o.legacyId, name: o.legacyName, typeDisplayName: o.typeName })) : [];

    const allData = [...v1Organizations, ...existingRecs];
    allData.forEach((org) => {
      const { typeDisplayName, id } = org;

      if (!grouped[typeDisplayName]) {
        grouped[typeDisplayName] = [];
      }

      grouped[typeDisplayName].push(org);

      if (allData.find((o) => o.legacyId === id && o.isPrimary)) {
        primaries[typeDisplayName] = id;
      }
    });

    allOrgs.current = allData;
    setGrouped(grouped);
    setPrimaries(primaries);
    onSelected?.(primaries);
  }, [v1Organizations, data]);

  useEffect(() => {
    if (!currentPrimaries && Object.keys(primaries).length > 0) {
      setCurrentPrimaries(primaries);
    }
  }, [currentPrimaries, primaries]);

  const handleSelectPrimary = (type, id) => {
    const newPrimaries = { ...primaries, [type]: id };
    setPrimaries(newPrimaries);
    onSelected?.(newPrimaries);
  };

  const getValues = () => {
    const primaryIds = Object.values(primaries);
    return allOrgs.current?.map((o) => ({
      legacyId: o?.id,
      legacyName: o?.name,
      type: o?.type,
      isPrimary: primaryIds.includes(o?.id),
    }));
  };

  const handleSave = () => {
    const newPrimaries = getValues();
    onSave?.(newPrimaries);
  };

  useImperativeHandle(ref, () => ({
    getValues,
  }));

  return (
    <div>
      <strong>Which v1 organization should be the primary?</strong>
      <br />
      {Object.entries(grouped).map(([type, orgs]) => (
        <div key={type}>
          <h6>{type}</h6>
          {orgs.map((org) => (
            <div key={org.id}>
              <RadioButton
                label={`${org.name} (${org.legacyId ? org.id : 'Linking Now'}${
                  currentPrimaries
                  && currentPrimaries?.[type] === org.id ? ' - Primary Now' : ''})`}
                name={type}
                checked={primaries[type] == org.id}
                onChange={() => handleSelectPrimary(type, org.id)}
              />
              {handleUnlink && (
                <BaseButton
                  icon='unlink'
                  iconOnly
                  onClick={() => handleUnlink(org)}
                  style={{ marginLeft: '0.5rem' }}
                  tooltip='Unlink'
                />
              )}
            </div>
          ))}
        </div>
      ))}
      {typeof onSave === 'function' && (
        <>
          <br />
          <BaseButton
            primary
            title='Save'
            icon='check'
            onClick={handleSave}
          />
        </>
      )}
    </div>
  );
});

export default OrganizationPrimarySelection;
